import {
    PauseRounded,
    PlayArrowRounded,
} from "@mui/icons-material";
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import {
    mdiFastForward5,
    mdiFastForward10,
    mdiFastForward15,
    mdiFastForward30,
    mdiFastForward45,
    mdiFastForward60,
    mdiRewind5,
    mdiRewind10,
    mdiRewind15,
    mdiRewind30,
    mdiRewind45,
    mdiRewind60,
} from '@mdi/js';
import Icon from '@mdi/react';
import { useEffect, useRef, useState } from "react";
import { fancyTimeFormat } from "./timeHelper";
import MoreWithPlaybackSpeedButton from "./MoreWithPlaybackSpeedButton";
import { readPlaybackSkipSettings } from "../utils/playbackSettings";
import { episodePlaybackLabel } from '../utils/episodePlaybackLabel';


const backwardSkipIconPaths = {
    5: mdiRewind5,
    10: mdiRewind10,
    15: mdiRewind15,
    30: mdiRewind30,
    45: mdiRewind45,
    60: mdiRewind60,
};

const forwardSkipIconPaths = {
    5: mdiFastForward5,
    10: mdiFastForward10,
    15: mdiFastForward15,
    30: mdiFastForward30,
    45: mdiFastForward45,
    60: mdiFastForward60,
};

const normalizedDuration = (duration) => (
    Number.isFinite(duration) && duration > 0 ? duration : 0
);

const boundedTime = (seconds, duration) => (
    Math.min(Math.max(Number.isFinite(seconds) ? seconds : 0, 0), duration)
);

const readPlaybackState = (audio) => {
    const durationSeconds = normalizedDuration(audio?.duration);
    return {
        durationSeconds,
        currentSeconds: boundedTime(audio?.currentTime, durationSeconds),
        playbackRate: Math.max(audio?.playbackRate || 1, 0.1),
        playing: Boolean(audio && !audio.paused),
    };
};

const usePlayback = (audio, onSeek) => {
    const [state, setState] = useState(() => readPlaybackState(audio));
    const resumeAfterScrubbing = useRef(null);

    useEffect(() => {
        if (!audio) {
            return undefined;
        }
        const syncPlaybackState = () => setState(readPlaybackState(audio));
        const playbackEvents = [
            'durationchange', 'loadedmetadata', 'emptied',
            'pause', 'play', 'ratechange', 'timeupdate',
        ];
        syncPlaybackState();
        playbackEvents.forEach(eventName => audio.addEventListener(eventName, syncPlaybackState));
        return () => {
            playbackEvents.forEach(eventName => audio.removeEventListener(eventName, syncPlaybackState));
            resumeAfterScrubbing.current = null;
        };
    }, [audio]);

    const ready = Boolean(audio && state.durationSeconds > 0);
    const seekTo = (seconds, startPlaying = false) => {
        const duration = normalizedDuration(audio?.duration);
        if (!audio || !duration) {
            return;
        }
        onSeek?.();
        audio.currentTime = boundedTime(seconds, duration);
        setState(readPlaybackState(audio));
        if (startPlaying) {
            audio.play().catch(() => {});
        }
    };
    const togglePlayPause = () => {
        if (!audio) {
            return;
        }
        if (audio.paused) {
            audio.play().catch(() => {});
        } else {
            audio.pause();
        }
    };
    const startScrubbing = (event) => {
        if (!ready || resumeAfterScrubbing.current !== null) {
            return;
        }
        onSeek?.();
        resumeAfterScrubbing.current = !audio.paused;
        if (event.pointerId != null) {
            event.currentTarget.setPointerCapture?.(event.pointerId);
        }
        if (resumeAfterScrubbing.current) {
            audio.pause();
        }
    };
    const finishScrubbing = () => {
        const shouldResume = resumeAfterScrubbing.current;
        resumeAfterScrubbing.current = null;
        if (shouldResume) {
            audio.play().catch(() => {});
        }
    };

    return {
        ...state,
        ready,
        remainingSeconds: Math.max(0, (state.durationSeconds - state.currentSeconds) / state.playbackRate),
        seekTo,
        togglePlayPause,
        startScrubbing,
        finishScrubbing,
    };
};

const PlaybackProgress = ({ playback, className }) => {
    const { currentSeconds, durationSeconds, remainingSeconds, ready, seekTo, startScrubbing, finishScrubbing } = playback;
    const progressPercent = ready ? (currentSeconds / durationSeconds) * 100 : 0;
    return (
        <input
            className={className}
            type='range'
            min='0'
            max={durationSeconds}
            step='0.1'
            value={currentSeconds}
            disabled={!ready}
            onChange={(event) => seekTo(Number(event.target.value))}
            onPointerDown={startScrubbing}
            onPointerUp={finishScrubbing}
            onPointerCancel={finishScrubbing}
            onLostPointerCapture={finishScrubbing}
            onBlur={finishScrubbing}
            aria-label='Episode position'
            aria-valuetext={ready
                ? `${fancyTimeFormat(currentSeconds)} elapsed, ${fancyTimeFormat(remainingSeconds)} remaining`
                : 'Audio duration unavailable'}
            style={{ '--playback-time-progress': `${progressPercent}%` }}
        />
    );
};

const PlaybackTimeDrawer = ({ audio, playback, open, onClose, skipSettings, onPlay }) => {
    const { currentSeconds, remainingSeconds, playing, ready, seekTo, togglePlayPause } = playback;
    const backwardLargeJump = skipSettings.backwardSeconds === 5
        ? 30 : skipSettings.backwardSeconds;
    const forwardLargeJump = skipSettings.forwardSeconds === 5
        ? 30 : skipSettings.forwardSeconds;

    const renderSkipButton = (seconds, direction) => (
        <button
            key={`${direction}-${seconds}`}
            type='button'
            className={`playbackTimeControlButton${seconds > 5 ? ' playbackTimeLargeJumpButton' : ''}`}
            onClick={() => seekTo(
                audio.currentTime + (direction === 'backward' ? -seconds : seconds),
                true,
            )}
            disabled={!ready}
            aria-label={`Skip ${direction} ${seconds} seconds`}
        >
            <Icon
                path={(direction === 'backward' ? backwardSkipIconPaths : forwardSkipIconPaths)[seconds]}
                aria-hidden='true'
                className='playbackTimeSeekIcon'
            />
        </button>
    );

    return (
        <SwipeableDrawer
            anchor='bottom'
            open={open}
            onClose={onClose}
            onOpen={() => { }}
            disableSwipeToOpen
            ModalProps={{
                keepMounted: false,
                BackdropProps: {
                    sx: {
                        backgroundColor: 'rgba(15, 23, 42, 0.36)',
                    },
                },
            }}
            PaperProps={{
                sx: {
                    borderRadius: '28px 28px 0px 0px',
                    boxShadow: '0 -16px 40px rgba(15, 23, 42, 0.14)',
                    overflow: 'hidden',
                },
            }}
        >
            <div
                className='playbackTimeDrawer horizontalCenter appDrawerSurface'
                role='dialog'
                aria-modal='true'
                aria-label='Playback time controls'
            >
                <div
                    className='playbackTimeDragIndicator'
                    aria-hidden='true'
                />
                <div className='playbackTimeSliderShell'>
                    <PlaybackProgress playback={playback} className='playbackTimeSlider' />
                    <div className='playbackTimeLabels' aria-hidden='true'>
                        <span>{fancyTimeFormat(currentSeconds)}</span>
                        <span>-{fancyTimeFormat(remainingSeconds)}</span>
                    </div>
                </div>

                <div className='playbackTimeControls'>
                    {renderSkipButton(backwardLargeJump, 'backward')}
                    {renderSkipButton(5, 'backward')}
                    <button
                        type='button'
                        className='playbackTimeControlButton playbackTimePlayButton'
                        onClick={() => { if (!playing) onPlay?.(); togglePlayPause(); }}
                        disabled={!audio}
                        aria-label={playing ? 'Pause' : 'Play'}
                    >
                        {playing ?
                            <PauseRounded aria-hidden='true' className='playbackTimePlayIcon' /> :
                            <PlayArrowRounded aria-hidden='true' className='playbackTimePlayIcon' />
                        }
                    </button>
                    {renderSkipButton(5, 'forward')}
                    {renderSkipButton(forwardLargeJump, 'forward')}
                </div>
            </div>
        </SwipeableDrawer>
    );
};

const PlayPauseBar = ({ onMoreClick, totalSeconds, showTranscript = false, hideSecondaryControls = false, onPlay,
    timeDrawerOpen, onTimeDrawerChange, onSeek }) => {
    const audio = document.getElementById('audio');
    const playback = usePlayback(audio, onSeek);
    const { durationSeconds, remainingSeconds, playing, ready, seekTo, togglePlayPause } = playback;
    const [skipSettings] = useState(readPlaybackSkipSettings);
    const [localTimeDrawerOpen, setLocalTimeDrawerOpen] = useState(false);
    const showTimeDrawer = timeDrawerOpen ?? localTimeDrawerOpen;
    const setShowTimeDrawer = onTimeDrawerChange || setLocalTimeDrawerOpen;
    const playLabel = episodePlaybackLabel(durationSeconds || totalSeconds, audio?.currentTime);

    const renderSkipButton = (seconds, direction) => (
        <button
            type='button'
            onClick={() => seekTo(audio.currentTime + (direction === 'backward' ? -seconds : seconds))}
            className='playPauseSkipButton'
            disabled={!ready}
            aria-label={`Skip ${direction} ${seconds} seconds`}
        >
            <Icon
                path={(direction === 'backward' ? backwardSkipIconPaths : forwardSkipIconPaths)[seconds]}
                aria-hidden='true'
                className='playPauseSkipIcon'
            />
        </button>
    );

    return (
        <>
            <div className='trackPlaybackControls'>
                {!showTranscript && !hideSecondaryControls && (
                    <div className='trackPlaybackTransportRow episodePlaybackGlass'>
                        {renderSkipButton(skipSettings.backwardSeconds, 'backward')}
                        <PlaybackProgress playback={playback} className='trackPlaybackProgress' />
                        {renderSkipButton(skipSettings.forwardSeconds, 'forward')}
                    </div>
                )}
                <div className='trackPlaybackActionRow episodePlaybackGlass'>
                    <button
                        type='button'
                        className='trackPlaybackPlayButton'
                        onClick={() => { if (!playing) onPlay?.(); togglePlayPause(); }}
                        disabled={!audio}
                        aria-label={playing ? 'Pause episode' : playLabel.accessibilityLabel}
                    >
                        {playing
                            ? <PauseRounded aria-hidden='true' />
                            : <PlayArrowRounded aria-hidden='true' />}
                    </button>
                    <button
                        type='button'
                        className='playPauseTimeButton'
                        onClick={() => setShowTimeDrawer(true)}
                        disabled={!ready}
                        aria-label={ready
                            ? `Adjust playback time, ${fancyTimeFormat(remainingSeconds)} remaining`
                            : 'Playback time unavailable'}
                        aria-haspopup='dialog'
                        aria-expanded={showTimeDrawer}
                    >
                        {ready ? `-${fancyTimeFormat(remainingSeconds)}` : '--:--'}
                    </button>
                    <MoreWithPlaybackSpeedButton
                        onMoreClick={onMoreClick}
                        className='trackPlaybackSpeedButton'
                    />
                </div>
            </div>
            <PlaybackTimeDrawer
                audio={audio}
                playback={playback}
                open={showTimeDrawer}
                onClose={() => setShowTimeDrawer(false)}
                skipSettings={skipSettings}
                onPlay={onPlay}
            />
        </>
    );
};

export default PlayPauseBar;
